// ─── Feedback boards ─────────────────────────────────────────────
//
// A Feedback is PCP's fourth backlog entity and the only LOCAL one: no Azure
// DevOps mirror, no external write, no connected account. Where a Feature /
// User Story / Bug is a row of text, a Feedback is a *canvas* — screenshots with
// comment pins anchored to exact points on them.
//
// Two ideas carry the whole model:
//
//  1. An anchored pin stores the image it belongs to plus a NORMALISED (u, v) in
//     that image's own box, never a world position. Move the screenshot and the
//     pin travels with it, still pointing at the same pixel — and because (u, v)
//     is unitless it survives any future rescale of the image too.
//
//  2. That anchor is deliberately machine-readable. The API resolves (u, v) back
//     into the screenshot's natural pixel space (`anchorPixelX/Y`), so a person
//     OR an agent reading the payload can say "this points at pixel (412, 690)
//     of screenshot 2" rather than "somewhere on the board".
//
// A pin is a THREAD: one root comment carrying the anchor + a lifecycle state,
// plus flat replies. The state paints the pin's ring, which is what makes a board
// scannable at a glance and what the detail page's to-do rail checks off.
// A value import, for re-validating a link URL arriving off the CLIPBOARD — see
// the `link` branch of `parseClipElement`. `feedback-link.ts` imports nothing, so
// this cannot cycle.
import { FEEDBACK_LINK_TITLE_MAX, parseLinkPaste } from './feedback-link.js';
// Value imports, for re-validating a connector arriving off the CLIPBOARD.
import { feedbackConnectorColorDef, parseConnectorEnd } from './feedback-connector.js';
// Value imports, for re-validating a text block arriving off the CLIPBOARD — see
// the `text` branch of `parseClipElement`. `feedback-text.ts` imports nothing
// either, so this cannot cycle.
import { FEEDBACK_TEXT_MIN_H, FEEDBACK_TEXT_MIN_W, feedbackTextBorderDef, feedbackTextFillDef, feedbackTextLeadingDef, sanitizeFeedbackRichText, } from './feedback-text.js';
export const FEEDBACK_COMMENT_STATES = [
    { key: 'new', label: 'New', ring: 'var(--fbc-new)' },
    { key: 'under_development', label: 'Under development', ring: 'var(--fbc-dev)' },
    { key: 'done', label: 'Done', ring: 'var(--fbc-done)', tint: 'var(--accent-success-soft)', terminal: true },
    { key: 'later', label: 'Later', ring: 'var(--fbc-later)', tint: 'var(--accent-info-soft)' },
];
export const FEEDBACK_COMMENT_STATE_KEYS = FEEDBACK_COMMENT_STATES.map((s) => s.key);
export function feedbackCommentStateDef(key) {
    return FEEDBACK_COMMENT_STATES.find((s) => s.key === key) ?? FEEDBACK_COMMENT_STATES[0];
}
/** The comment state that means "handled" — the one the to-do checkbox sets. */
export const FEEDBACK_COMMENT_DONE = 'done';
/** Where an unchecked thread lands. */
export const FEEDBACK_COMMENT_OPEN = 'new';
/** Ordered worst-first. Every colour aliases a token that already exists in all 5
 *  palettes, both modes and both high-contrast blocks, so severity needs no
 *  per-theme table of its own. `development` sits deliberately OFF the severity
 *  ramp — it means "internal engineering work", not "less bad than not serious" —
 *  which is why it borrows the app's Feature purple rather than a hotter hue. */
export const FEEDBACK_SEVERITIES = [
    { key: 'critical', label: 'Critical', color: 'var(--accent-error)', tint: 'var(--accent-error-soft)' },
    { key: 'serious', label: 'Serious', color: 'var(--accent-warning)', tint: 'var(--accent-warning-soft)' },
    { key: 'moderate', label: 'Moderate', color: 'var(--accent-info)', tint: 'var(--accent-info-soft)' },
    { key: 'not_serious', label: 'Not serious', color: 'var(--text-tertiary)', tint: 'var(--bg-hover)' },
    { key: 'development', label: 'Development', color: 'var(--type-feature)', tint: 'var(--type-feature-soft)' },
];
export const FEEDBACK_SEVERITY_KEYS = FEEDBACK_SEVERITIES.map((s) => s.key);
export const DEFAULT_FEEDBACK_SEVERITY = 'moderate';
export function feedbackSeverityDef(key) {
    return FEEDBACK_SEVERITIES.find((s) => s.key === key)
        ?? FEEDBACK_SEVERITIES.find((s) => s.key === DEFAULT_FEEDBACK_SEVERITY);
}
/** Seeded to match an Azure DevOps **Bug**, which is what was asked for: a
 *  Feedback starts life behaving exactly like the bug it will probably become. */
export const DEFAULT_FEEDBACK_STATES = [
    { name: 'New', category: 'new' },
    { name: 'Active', category: 'active' },
    { name: 'Resolved', category: 'done', terminal: true },
    { name: 'Closed', category: 'done', terminal: true },
];
/**
 * The board's state flow, resolved.
 *
 * The ONE place that answers "what states exist", because the two ends had
 * different answers and it showed: `GET /api/settings` returns the RAW settings
 * rows, so `feedbackStates` is simply absent until an admin saves the section —
 * while the API resolves a requested state against `getAppSettings()`, which IS
 * defaults-merged. The server therefore accepted Active/Resolved/Closed while the
 * detail page's dropdown, reading `?? []`, offered nothing but the board's own
 * current state. (b-01844)
 *
 * Every reader goes through here so an unconfigured install behaves like the
 * seeded flow on both sides, with no admin action required.
 */
export function resolveFeedbackStates(configured) {
    return configured?.length ? configured : DEFAULT_FEEDBACK_STATES;
}
/** First non-terminal state = where a new board starts. */
export function initialFeedbackState(defs) {
    const list = resolveFeedbackStates(defs);
    return (list.find((s) => !s.terminal) ?? list[0]).name;
}
export function feedbackStateLabel(name, defs) {
    return defs?.find((s) => s.name === name)?.label?.trim() || name;
}
// ─── Identity ────────────────────────────────────────────────────
/** Human short id, mirroring the knowledge base's `kb-<n>`. */
export function feedbackShortId(number) {
    return `fb-${number}`;
}
/** Parse `fb-12` / `FB-12` / bare `12` out of a search box. */
export function parseFeedbackShortId(raw) {
    const m = /^\s*(?:fb-)?(\d{1,9})\s*$/i.exec(raw);
    return m ? Number(m[1]) : null;
}
// ─── Comment references (f-02016) ────────────────────────────────
//
// Every comment THREAD (a root comment = a pin on the canvas) carries a permanent,
// board-local number 1..999, written `c-001`. Every REPLY carries a permanent
// number 1..99 within its thread, written `c-001-1`. Numbers are issued in
// creation order and never reused: deleting c-003 leaves a gap.
//
// A reference means the WHOLE THREAD by default — `c-003` is the pin and all its
// replies. `c-003-2` names that one reply, and is used only when that one message
// is meant. Combined with the board's short id it reads `fb-35 c-003`.
/** Most comment threads one board may hold; the next one is refused. */
export const FEEDBACK_THREAD_MAX = 999;
/** Most replies one thread may hold; the next one is refused. */
export const FEEDBACK_REPLY_MAX = 99;
/** `3` → `c-003`. Always at least three digits; never truncates (`1000` → `c-1000`). */
export function feedbackThreadRef(threadNumber) {
    return `c-${String(threadNumber).padStart(3, '0')}`;
}
/** `(3, 1)` → `c-003-1`. The reply part is NOT padded. */
export function feedbackReplyRef(threadNumber, replyNumber) {
    return `${feedbackThreadRef(threadNumber)}-${replyNumber}`;
}
const COMMENT_REF_RE = /^#?\s*c-(\d{1,6})(?:-(\d{1,6}))?$/i;
/**
 * Parse a comment reference: `c-003`, `c-3`, `C-003`, `c-003-1`, `#c-003`,
 * surrounding whitespace allowed. Thread must be 1..999 and reply 1..99 —
 * anything else (`c-000`, `c-1000`, `c-003-0`, `c-003-100`, junk) → null.
 * Leading zeros are tolerated in both parts (`c-3`, `c-003-01`). `reply` is
 * null for a thread reference.
 */
export function parseFeedbackCommentRef(s) {
    if (typeof s !== 'string')
        return null;
    const m = COMMENT_REF_RE.exec(s.trim());
    if (!m)
        return null;
    const thread = Number(m[1]);
    if (!Number.isInteger(thread) || thread < 1 || thread > FEEDBACK_THREAD_MAX)
        return null;
    if (m[2] === undefined)
        return { thread, reply: null };
    const reply = Number(m[2]);
    if (!Number.isInteger(reply) || reply < 1 || reply > FEEDBACK_REPLY_MAX)
        return null;
    return { thread, reply };
}
/** `('fb-35', 'c-003')` → `fb-35 c-003` — how a person writes a comment on a board. */
export function feedbackCommentFullRef(boardShortId, commentRef) {
    return `${boardShortId} ${commentRef}`;
}
const FULL_REF_RE = /^fb-(\d{1,9})\s*(?:[,/]\s*|\s+)(.+)$/i;
/**
 * Parse a board + comment reference: `fb-35 c-003`, `fb-35, c-003`,
 * `fb-35/c-003`, `fb-35 c-003-1`, case-insensitive. Null when either half is
 * not a valid reference.
 */
export function parseFeedbackFullRef(s) {
    if (typeof s !== 'string')
        return null;
    const m = FULL_REF_RE.exec(s.trim());
    if (!m)
        return null;
    const board = Number(m[1]);
    if (!Number.isInteger(board) || board < 1)
        return null;
    const ref = parseFeedbackCommentRef(m[2]);
    return ref ? { board, ...ref } : null;
}
// ─── Canvas geometry ─────────────────────────────────────────────
/** Max world width a pasted/uploaded screenshot is scaled to on insert, so a 4K
 *  grab and a phone grab land on the board at comparable sizes. Aspect preserved. */
export const FEEDBACK_IMAGE_MAX_WORLD_W = 900;
/**
 * The rendition ladder for canvas screenshots, in source pixels.
 *
 * A board is mostly grabs of application UI, so two things are in tension: a
 * board full of full-size originals is heavy, and a downscaled screenshot is
 * unreadable — the moment text is resampled it turns to mush. The ladder settles
 * it by zoom: the canvas asks for the rung just above the pixels it is actually
 * painting, so a board at overview zoom pulls thumbnails and the same board
 * zoomed into one dialog pulls that screenshot at native resolution.
 *
 * It runs well past the site-wide `IMG_WIDTH_LADDER` (which caps at 1600, sized
 * for a ~1000px content column) because a canvas legitimately zooms past 1:1 on
 * a HiDPI screen. imgproxy never enlarges, so a rung above a screenshot's own
 * width simply returns the source — which is why the ladder is always clamped to
 * `naturalWidth` when the variants are built.
 */
export const FEEDBACK_IMG_LADDER = [480, 960, 1440, 1920, 2560, 3840];
/** Quality for screenshot renditions. Higher than the site default (82): these
 *  are screenshots of text, where lossy artefacts land exactly on the glyph edges
 *  that make the screenshot worth keeping. */
export const FEEDBACK_IMG_QUALITY = 92;
/**
 * The rendition to paint for a screenshot currently occupying `cssWidth` CSS
 * pixels on screen, at device pixel ratio `dpr`.
 *
 * Picks the first rung at or above the real device-pixel demand so text is never
 * upsampled, and falls back to the full-size original when the imgproxy pipeline
 * is off (`variants` empty) or when demand exceeds the top rung.
 */
export function pickFeedbackVariant(image, cssWidth, dpr = 1) {
    if (!image.variants?.length)
        return image.url;
    const need = Math.max(1, Math.ceil(cssWidth * (Number.isFinite(dpr) && dpr > 0 ? dpr : 1)));
    const ascending = image.variants.slice().sort((a, b) => a.width - b.width);
    return (ascending.find((v) => v.width >= need) ?? ascending[ascending.length - 1]).url;
}
/** Description cap. Enforced server-side; the editor shows the counter. */
export const FEEDBACK_DESCRIPTION_MAX = 8_000;
/** Comment/reply body cap — generous, but not unbounded. */
export const FEEDBACK_COMMENT_MAX = 8_000;
/**
 * What a comment box holds once a send that FAILED gives its text back. (b-02030)
 *
 * The box was emptied as the send started, so usually it is still empty and the
 * failed text simply returns. But the person may have typed a NEW reply while the
 * old one was in flight — and neither may be lost: keeping only the newer one, as
 * this used to, dropped the failed reply without a word. So both are kept, the
 * failed one FIRST: it was written first, and the caret, which sits at the end of
 * what is being typed, stays at the end of it.
 *
 * Idempotent — the same text is handed back to the live box and to its stored
 * draft, and either may already hold it.
 */
export function restoreUnsentText(current, failed) {
    if (!failed.trim())
        return current;
    if (!current.trim())
        return failed;
    const head = `${failed.trimEnd()}\n\n`;
    // Already in there: typed again word for word, or given back once before.
    if (current.trim() === failed.trim() || current.startsWith(head))
        return current;
    return head + current.trimStart();
}
/**
 * Where a board's NEW-comment drafts are stored — one part of their draft key.
 * (b-02056)
 *
 * The board's id, except for a feature/bug request's board, which is keyed by its
 * REQUEST. That board lives client-side as an unsaved draft (empty id) until the
 * first mark creates it, and the surface then remounts on the real one: keyed by
 * board id, the text a failed first comment left behind sat under the draft's key
 * and was never offered again. The request is the same on both sides of that
 * remount — and on the full-board page of the same board.
 */
export function feedbackDraftScope(board) {
    return board.requestId ? `request:${board.requestId}` : board.id;
}
/**
 * What an open edit row holds once a save of the SAME comment fails — a save made
 * from a thread that was closed while it was in flight. (b-02056)
 *
 * A row still holding the text it opened with takes the failed edit. A row the
 * person has started changing again keeps its own, newer text. Never merged, as a
 * reply is (`restoreUnsentText`): an edit is a whole body, and two bodies joined
 * would be saved as one.
 */
export function restoreFailedEdit(current, openedWith, failed) {
    return current === openedWith && failed.trim() ? failed : current;
}
export function encodeKeptFeedbackEdit(kept) {
    return JSON.stringify({ text: kept.text, baseBody: kept.baseBody, baseEditedAt: kept.baseEditedAt });
}
/** A stored kept edit, or null for nothing, an empty edit, or anything malformed. */
export function decodeKeptFeedbackEdit(raw) {
    if (!raw)
        return null;
    try {
        const v = JSON.parse(raw);
        if (!v || typeof v !== 'object' || typeof v.text !== 'string' || !v.text.trim() || typeof v.baseBody !== 'string')
            return null;
        return { text: v.text, baseBody: v.baseBody, baseEditedAt: typeof v.baseEditedAt === 'string' ? v.baseEditedAt : null };
    }
    catch {
        return null;
    }
}
/**
 * A kept edit, against the comment as it reads NOW:
 *  - `landed`  — the comment already says it (the save landed after all, or the
 *                same words were saved since). Nothing to offer; drop it.
 *  - `current` — the comment is still the version the save would have replaced.
 *                Offer it back as it is: Save is just the retry.
 *  - `changed` — the comment changed since. Offer it only under the edit-conflict
 *                warning, so saving over the newer text is a choice, not an
 *                accident.
 */
export function keptFeedbackEditStatus(kept, now) {
    if (now.body.trim() === kept.text.trim())
        return 'landed';
    return now.body === kept.baseBody && (now.bodyEditedAt ?? null) === kept.baseEditedAt ? 'current' : 'changed';
}
export function encodeUnsentFeedbackComment(unsent) {
    const s = unsent.spot;
    return JSON.stringify({ text: unsent.text, spot: { imageId: s.imageId, anchorU: s.anchorU, anchorV: s.anchorV, x: s.x, y: s.y } });
}
const numOrNull = (v) => v === null || (typeof v === 'number' && Number.isFinite(v));
/** A stored unsent comment, or null for nothing, empty text, or anything malformed. */
export function decodeUnsentFeedbackComment(raw) {
    if (!raw)
        return null;
    try {
        const v = JSON.parse(raw);
        const s = v?.spot;
        if (!v || typeof v.text !== 'string' || !v.text.trim() || !s || typeof s !== 'object')
            return null;
        if (!(s.imageId === null || typeof s.imageId === 'string'))
            return null;
        if (!numOrNull(s.anchorU) || !numOrNull(s.anchorV))
            return null;
        if (typeof s.x !== 'number' || !Number.isFinite(s.x) || typeof s.y !== 'number' || !Number.isFinite(s.y))
            return null;
        return { text: v.text, spot: { imageId: s.imageId, anchorU: s.anchorU, anchorV: s.anchorV, x: s.x, y: s.y } };
    }
    catch {
        return null;
    }
}
/** The same pin spot — exactly: what decides whether kept text is shown as
 *  written somewhere else. */
export function sameFeedbackPinSpot(a, b) {
    if (!a || !b)
        return false;
    return a.imageId === b.imageId && a.anchorU === b.anchorU && a.anchorV === b.anchorV && a.x === b.x && a.y === b.y;
}
export const FEEDBACK_SCOPES = ['library', 'request', 'all'];
/** The scope a read gets when it does not ask for one. Deliberately the narrow
 *  one: a new caller that forgets the parameter leaks nothing. */
export const DEFAULT_FEEDBACK_SCOPE = 'library';
export function coerceFeedbackScope(value) {
    return FEEDBACK_SCOPES.includes(value)
        ? value
        : DEFAULT_FEEDBACK_SCOPE;
}
// ─── A backlog item's Feedback tab ───────────────────────────────
//
// The boards linked to ONE Feature / User Story / Bug, listed on that item's own
// Feedback tab. Deliberately NOT the list above with a filter bolted on: this
// list has no filters at all and hides nothing — a linked board shows up whatever
// state it is in, including the terminal ones the Backlog area's list hides by
// default. The only choice is the order. (f-01920)
export const BACKLOG_FEEDBACK_SORTS = ['updated', 'opened', 'title'];
export const DEFAULT_BACKLOG_FEEDBACK_SORT = 'updated';
/** Human labels for the tab's sort menu. */
export const BACKLOG_FEEDBACK_SORT_LABELS = {
    updated: 'Last updated',
    opened: 'Latest opened',
    title: 'Alphabetic',
};
export function coerceBacklogFeedbackSort(value) {
    return BACKLOG_FEEDBACK_SORTS.includes(value)
        ? value
        : DEFAULT_BACKLOG_FEEDBACK_SORT;
}
/** Read one item's remembered sort out of the map, defaulting anything absent or
 *  unrecognised. The single reader, so client and server can never disagree. */
export function backlogFeedbackSortFor(prefs, workItemId) {
    return coerceBacklogFeedbackSort(prefs?.[String(workItemId)]);
}
/** Keep only well-formed entries — the map is user-writable JSONB, so a reader
 *  must never trust its shape. */
export function coerceBacklogFeedbackSortPrefs(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (!/^\d{1,12}$/.test(k))
            continue;
        if (v === DEFAULT_BACKLOG_FEEDBACK_SORT)
            continue; // default is stored as absence
        if (BACKLOG_FEEDBACK_SORTS.includes(v)) {
            out[k] = v;
        }
    }
    return out;
}
export const FEEDBACK_GRID_TYPES = [
    { key: 'dots', label: 'Dots', hint: 'A dot at each grid intersection' },
    { key: 'lines', label: 'Lines', hint: 'A full grid of hairlines' },
    { key: 'none', label: 'None', hint: 'A plain surface' },
];
/** Grid spacing in world units. Also the step "snap to guides" snaps to, so the
 *  visible grid and the snapping grid can never disagree. */
export const FEEDBACK_GRID_STEP = 52;
export const FEEDBACK_SNAP_KEYS = ['guides', 'objects', 'alignments'];
/**
 * Thresholds are in SCREEN pixels, never world units.
 *
 * A world-unit threshold would mean "snaps from 8 units away", which at 20% zoom
 * is under two pixels of pointer travel and at 200% is a fifth of the screen —
 * the same number feeling broken at both ends. In screen pixels the setting means
 * what the user experiences: "snaps when it looks within 8px of lining up".
 */
export const FEEDBACK_SNAPS = [
    {
        key: 'guides',
        label: 'Snap to guides',
        hint: 'Settle onto the canvas grid.',
        defaultEnabled: true,
        defaultThreshold: 8,
    },
    {
        key: 'objects',
        label: 'Snap to objects',
        hint: 'Butt up flush against another screenshot’s edge.',
        defaultEnabled: false,
        defaultThreshold: 8,
    },
    {
        key: 'alignments',
        label: 'Snap to alignments',
        hint: 'Line up with another screenshot’s top, middle, bottom, left, centre or right — and show the guide while you drag.',
        defaultEnabled: true,
        defaultThreshold: 8,
    },
];
export const FEEDBACK_SNAP_THRESHOLD_MIN = 1;
export const FEEDBACK_SNAP_THRESHOLD_MAX = 40;
export const DEFAULT_FEEDBACK_CANVAS_PREFS = {
    grid: 'dots',
    snap: {
        guides: { enabled: true, threshold: 8 },
        objects: { enabled: false, threshold: 8 },
        alignments: { enabled: true, threshold: 8 },
    },
    perBoard: {},
};
const clampThreshold = (v, fallback) => {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : fallback;
    return Math.min(FEEDBACK_SNAP_THRESHOLD_MAX, Math.max(FEEDBACK_SNAP_THRESHOLD_MIN, n));
};
/** Coerce whatever is stored in `authUsers.preferences.feedbackCanvas` into a
 *  complete, valid prefs object. Shared so the API and the SPA cannot disagree
 *  about what a half-written or legacy blob means. */
export function coerceFeedbackCanvasPrefs(raw) {
    const d = DEFAULT_FEEDBACK_CANVAS_PREFS;
    const o = (raw ?? {});
    const grid = FEEDBACK_GRID_TYPES.some((g) => g.key === o.grid) ? o.grid : d.grid;
    const rawSnap = (o.snap ?? {});
    const snap = {};
    for (const def of FEEDBACK_SNAPS) {
        const cur = (rawSnap[def.key] ?? {});
        snap[def.key] = {
            enabled: typeof cur.enabled === 'boolean' ? cur.enabled : def.defaultEnabled,
            threshold: clampThreshold(cur.threshold, def.defaultThreshold),
        };
    }
    const rawPer = (o.perBoard ?? {});
    const perBoard = {};
    for (const [boardId, v] of Object.entries(rawPer)) {
        const entry = (v ?? {});
        const out = {};
        for (const k of FEEDBACK_SNAP_KEYS)
            if (typeof entry[k] === 'boolean')
                out[k] = entry[k];
        if (Object.keys(out).length)
            perBoard[boardId] = out;
    }
    return { grid, snap, perBoard };
}
/** The snap settings in force for one board: the user's defaults, with that
 *  board's own on/off overrides applied. Thresholds are never per-board — the
 *  toggle is the per-board decision, the feel is a personal one. */
export function effectiveFeedbackSnap(prefs, boardId) {
    const over = (boardId && prefs.perBoard[boardId]) || {};
    const out = {};
    for (const k of FEEDBACK_SNAP_KEYS) {
        out[k] = { ...prefs.snap[k], enabled: over[k] ?? prefs.snap[k].enabled };
    }
    return out;
}
export const NO_CROP = { u: 0, v: 0, w: 1, h: 1 };
/** The smallest window a crop may shrink to, as a fraction — below this the
 *  handles overlap and the element becomes impossible to grab. */
export const FEEDBACK_CROP_MIN = 0.05;
export function isCropped(c) {
    return c.u > 0 || c.v > 0 || c.w < 1 || c.h < 1;
}
/** Clamp an arbitrary window into a legal one: inside the image, at least
 *  FEEDBACK_CROP_MIN on each axis. Shared so the API and the canvas agree on what
 *  a legal crop is rather than each having an opinion. */
export function clampCrop(raw) {
    const n = (v, fallback) => typeof v === 'number' && Number.isFinite(v) ? v : fallback;
    let u = Math.min(1 - FEEDBACK_CROP_MIN, Math.max(0, n(raw?.u, 0)));
    let v = Math.min(1 - FEEDBACK_CROP_MIN, Math.max(0, n(raw?.v, 0)));
    const w = Math.min(1 - u, Math.max(FEEDBACK_CROP_MIN, n(raw?.w, 1)));
    const h = Math.min(1 - v, Math.max(FEEDBACK_CROP_MIN, n(raw?.h, 1)));
    // Re-clamp the origin in case a large w/h pushed the window past the edge.
    u = Math.min(u, 1 - w);
    v = Math.min(v, 1 - h);
    return { u, v, w, h };
}
/** Where an image-space anchor sits inside the element's box, 0..1 — or null when
 *  the crop has hidden it. */
export function anchorToBox(anchorU, anchorV, crop) {
    const u = (anchorU - crop.u) / crop.w;
    const v = (anchorV - crop.v) / crop.h;
    if (u < 0 || u > 1 || v < 0 || v > 1)
        return null;
    return { u, v };
}
/** The reverse: a box position (what a drag produces) back into image space, which
 *  is what gets stored. */
export function boxToAnchor(boxU, boxV, crop) {
    return { u: crop.u + boxU * crop.w, v: crop.v + boxV * crop.h };
}
/**
 * The element's aspect ratio (height ÷ width) for a given crop.
 *
 * The visible window is `cropW × naturalWidth` by `cropH × naturalHeight`, so the
 * box must follow that ratio or the picture stretches — the `<img>` is drawn with
 * `object-fit: fill` precisely so the box and the bitmap always agree, which only
 * holds if the box is kept in the right shape.
 */
export function croppedAspect(naturalWidth, naturalHeight, crop) {
    const nw = naturalWidth && naturalWidth > 0 ? naturalWidth : 3;
    const nh = naturalHeight && naturalHeight > 0 ? naturalHeight : 2;
    return (nh * crop.h) / (nw * crop.w);
}
/** Minimum on-canvas size for a screenshot, in world units. Small enough to tuck a
 *  detail shot in a corner, large enough to stay grabbable. */
export const FEEDBACK_IMAGE_MIN_WORLD_W = 60;
/**
 * A resize from one corner, aspect locked.
 *
 * The DRAGGED corner follows the pointer and the OPPOSITE corner stays put — which
 * is what makes a corner drag feel like a corner drag. The aspect is taken from the
 * current crop, so resizing a cropped screenshot cannot un-square it.
 */
export function resizeFromCorner(box, corner, pointer, aspect) {
    // The anchor is the corner diagonally opposite the one being dragged.
    const fixed = {
        x: corner === 'nw' || corner === 'sw' ? box.x + box.w : box.x,
        y: corner === 'nw' || corner === 'ne' ? box.y + box.h : box.y,
    };
    const west = corner === 'nw' || corner === 'sw';
    const north = corner === 'nw' || corner === 'ne';
    // Width is the driver and height follows the aspect: with both free, rounding on
    // the two axes fights and the element visibly shimmers as you drag.
    let w = Math.abs(fixed.x - pointer.x);
    if (w < FEEDBACK_IMAGE_MIN_WORLD_W)
        w = FEEDBACK_IMAGE_MIN_WORLD_W;
    const h = w * aspect;
    return {
        x: west ? fixed.x - w : fixed.x,
        y: north ? fixed.y - h : fixed.y,
        w,
        h,
    };
}
// ─── Naming ──────────────────────────────────────────────────────
//
// The entity is a *board*: a canvas of screenshots with comment threads pinned to
// them. "Feedback" alone named the topic rather than the thing, so every surface
// reads these instead of hand-writing the word. (f-01858)
export const FEEDBACK_BOARD_LABEL = 'Feedback Board';
export const FEEDBACK_BOARD_LABEL_PLURAL = 'Feedback Boards';
export const FEEDBACK_LAYER_ACTIONS = [
    { key: 'front', label: 'Bring to front' },
    { key: 'forward', label: 'Bring forward' },
    { key: 'backward', label: 'Send backward' },
    { key: 'back', label: 'Send to back' },
];
/**
 * Reorder one element within its own kind and return the new `z` of every element
 * that changed — the caller persists exactly those.
 *
 * Order is **renormalised to 0..n-1 on every action**, which is what keeps this
 * honest over a long-lived board: nudging with fractional midpoints (the other
 * common approach) drifts toward float precision and eventually two elements
 * collide with no way to separate them. Renormalising costs one extra patch per
 * displaced element and can never degrade.
 *
 * Ties in the stored `z` are broken by the given order, so a board whose rows all
 * carry the seeded `z = 0` still reorders predictably instead of shuffling.
 */
export function applyLayerAction(items, id, action) {
    const sorted = items
        .map((item, i) => ({ item, i }))
        .sort((a, b) => (a.item.z - b.item.z) || (a.i - b.i))
        .map(({ item }) => item);
    const from = sorted.findIndex((s) => s.id === id);
    if (from < 0)
        return [];
    const to = action === 'front' ? sorted.length - 1
        : action === 'back' ? 0
            : action === 'forward' ? Math.min(sorted.length - 1, from + 1)
                : Math.max(0, from - 1);
    if (to === from)
        return [];
    const [moved] = sorted.splice(from, 1);
    sorted.splice(to, 0, moved);
    // Only the span between the two positions can have shifted, but comparing every
    // element against its stored z is simpler and returns the same set.
    return sorted
        .map((item, z) => ({ id: item.id, z }))
        .filter(({ id: itemId, z }) => items.find((i) => i.id === itemId)?.z !== z);
}
/** True when `action` would do nothing — the element is already at that end. Used
 *  to omit dead entries from the context menu rather than showing them disabled. */
export function canLayer(items, id, action) {
    return applyLayerAction(items, id, action).length > 0;
}
// ─── Sticky notes ────────────────────────────────────────────────
//
// The canvas's third element type (f-01859). A freeform note you type straight
// into — not an annotation of anything, which is what separates it from a comment
// pin: a pin points at a pixel of a screenshot, a note just sits on the board.
//
// It behaves "like a screenshot" in the ways that matter — dragged, resized,
// layered, deletable — so it shares the screenshot stacking band and the same
// snap engine. Two things differ deliberately:
//
//   - **Resize is FREE.** A screenshot is aspect-locked because stretching a
//     bitmap is always wrong; a note is a box of text, so both axes move
//     independently. See `resizeFreeFromCorner`.
//   - **The colour is the user's, not the theme's.** The 16 fills below are a
//     decorative choice (Miro's model), so unlike every accent in the app they do
//     NOT shift with the active palette — only between light and dark, for
//     legibility. They are the one place in the app where a colour is not
//     semantic, which is why they get their own `--fbn-*` token family rather
//     than aliasing an accent.
/** Width ÷ height a new note starts at. Resizing is free from then on. */
export const FEEDBACK_NOTE_DEFAULT_ASPECT = 3 / 2;
/** World width of a freshly placed note — a comfortable reading measure at the
 *  zoom levels boards are actually worked at. */
export const FEEDBACK_NOTE_DEFAULT_W = 320;
/** Smallest a note can be dragged to. Below this the text is unreadable and the
 *  handles overlap each other. */
export const FEEDBACK_NOTE_MIN_W = 80;
export const FEEDBACK_NOTE_MIN_H = 60;
/** The note's text ceiling, in world units (the canvas scales it like everything
 *  else). Auto-fit only ever shrinks BELOW this. */
export const FEEDBACK_NOTE_MAX_PT = 48;
/** Floor for auto-fit: past this the text is illegible, so it clips instead —
 *  better to show a too-small note than to pretend an essay fits on a sticky. */
export const FEEDBACK_NOTE_MIN_PT = 7;
export const FEEDBACK_NOTE_BODY_MAX = 2000;
/**
 * The 16 note fills, in the order the swatch strip shows them: warm through cool
 * through neutral, so picking by eye is picking along a line rather than hunting a
 * grid. Keys are stable slugs — the label may be reworded, the stored value never
 * changes.
 */
export const FEEDBACK_NOTE_COLORS = [
    { key: 'lemon', label: 'Lemon', token: '--fbn-lemon' },
    { key: 'butter', label: 'Butter', token: '--fbn-butter' },
    { key: 'apricot', label: 'Apricot', token: '--fbn-apricot' },
    { key: 'coral', label: 'Coral', token: '--fbn-coral' },
    { key: 'rose', label: 'Rose', token: '--fbn-rose' },
    { key: 'plum', label: 'Plum', token: '--fbn-plum' },
    { key: 'violet', label: 'Violet', token: '--fbn-violet' },
    { key: 'indigo', label: 'Indigo', token: '--fbn-indigo' },
    { key: 'sky', label: 'Sky', token: '--fbn-sky' },
    { key: 'teal', label: 'Teal', token: '--fbn-teal' },
    { key: 'mint', label: 'Mint', token: '--fbn-mint' },
    { key: 'sage', label: 'Sage', token: '--fbn-sage' },
    { key: 'olive', label: 'Olive', token: '--fbn-olive' },
    { key: 'sand', label: 'Sand', token: '--fbn-sand' },
    { key: 'stone', label: 'Stone', token: '--fbn-stone' },
    { key: 'slate', label: 'Slate', token: '--fbn-slate' },
];
export const FEEDBACK_NOTE_DEFAULT_COLOR = 'lemon';
/** A stored colour → its definition, falling back to the default so an unknown
 *  value (an older row, a hand-written API call) still paints. */
export function feedbackNoteColorDef(key) {
    return FEEDBACK_NOTE_COLORS.find((c) => c.key === key) ?? FEEDBACK_NOTE_COLORS[0];
}
/**
 * Resize a box from one corner with **no aspect lock** — the note counterpart to
 * `resizeFromCorner`.
 *
 * Simpler than the aspect-locked version in the way that matters: because the two
 * axes are independent, the pointer's x drives the width and its y drives the
 * height, with no conversion between them. That is what removes the whole class of
 * bug the locked version needed two fixes for (b-01865, b-01867) — there is no
 * amplification to bound and no axis to choose between.
 */
export function resizeFreeFromCorner(box, corner, pointer, minW = FEEDBACK_NOTE_MIN_W, minH = FEEDBACK_NOTE_MIN_H) {
    const west = corner === 'nw' || corner === 'sw';
    const north = corner === 'nw' || corner === 'ne';
    // The anchor is the corner diagonally opposite the one being dragged.
    const fixedX = west ? box.x + box.w : box.x;
    const fixedY = north ? box.y + box.h : box.y;
    const w = Math.max(minW, Math.abs(fixedX - pointer.x));
    const h = Math.max(minH, Math.abs(fixedY - pointer.y));
    return {
        x: west ? fixedX - w : fixedX,
        y: north ? fixedY - h : fixedY,
        w,
        h,
    };
}
// ─── Copy / paste of a canvas element (f-01875) ───────────────────
//
// The OS CLIPBOARD is the only state here — there is deliberately no in-memory
// "last copied" slot.
//
// That is not a detail. A private slot means ⌘C silently stops behaving like ⌘C:
// the copy does not survive a reload, cannot cross to another board or tab, and —
// worst — has to GUESS whether an image sitting on the real clipboard is newer
// than the private copy. With the payload on the clipboard there is nothing to
// guess: one slot, most recent write wins, which is what every other app on the
// machine already does.
//
// Three representations are written together, the shape Excalidraw and Figma both
// use, so normal copying is enriched rather than hijacked:
//
//   text/plain  — the readable thing (a note's text, a screenshot's alt). This is
//                 what any OTHER app receives, so ⌘C into Slack still does the
//                 obvious thing.
//   <MIME>      — the real payload, on a private type. `DataTransfer.setData`
//                 accepts arbitrary type strings and round-trips them between
//                 browser tabs; it is legacy behaviour browsers keep on purpose.
//   text/html   — the SAME payload hidden in a data attribute (Figma's trick).
//                 Private types are not mapped onto the native clipboard, so they
//                 can be dropped when the OS gets involved; text/html always
//                 survives. Belt and braces, and an invisible span is harmless
//                 wherever else it lands.
//
// Read back in the `paste` EVENT via `clipboardData.getData` — synchronous and
// permission-free. `navigator.clipboard.read()` would also work and is the only
// way to reach the newer `web `-prefixed custom formats, but it is Chromium-only
// and prompts for clipboard-read access on every paste, which is a terrible trade
// for something bound to ⌘V.
/** Our private clipboard type. Suffixed `+json` per convention. */
export const FEEDBACK_CLIP_MIME = 'application/x-pcp-feedback+json';
/** Marks a payload as ours AND as this shape. A future change bumps `version`
 *  rather than guessing at an older payload's meaning. */
const FEEDBACK_CLIP_TAG = 'pcp/feedback-element';
const FEEDBACK_CLIP_VERSION = 1;
/**
 * Serialise a copied selection for the clipboard.
 *
 * `element` is written ALONGSIDE `elements`, holding the first one, and the
 * version deliberately stays 1. `decodeFeedbackClip` rejects a version it does
 * not recognise, so a bump would stand the skew on its head: every clip already
 * sitting on a clipboard would become unpasteable the moment the deploy landed,
 * and every tab still running the previous bundle would refuse what this one
 * copies. Additive costs one key and neither of those. (f-01885)
 */
export function encodeFeedbackClip(boardId, elements) {
    return JSON.stringify({
        tag: FEEDBACK_CLIP_TAG, version: FEEDBACK_CLIP_VERSION, boardId,
        element: elements[0],
        elements,
    });
}
/**
 * Parse a clipboard payload back, or null if this is not one of ours.
 *
 * Everything arriving here came off the clipboard, so it is treated as hostile
 * input rather than as something we wrote: the tag, the version, the element kind
 * and every field are checked, and anything unrecognised returns null instead of
 * a half-built element. A crafted `imageId` is harmless regardless — the server
 * only ever duplicates a row that belongs to the board being pasted into.
 */
export function decodeFeedbackClip(raw) {
    if (!raw)
        return null;
    let v;
    try {
        v = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!v || typeof v !== 'object')
        return null;
    const o = v;
    if (o.tag !== FEEDBACK_CLIP_TAG || o.version !== FEEDBACK_CLIP_VERSION)
        return null;
    if (typeof o.boardId !== 'string' || !o.boardId)
        return null;
    // Either shape: `elements` from this bundle, a lone `element` from one running
    // before this deploy.
    const raws = Array.isArray(o.elements)
        ? o.elements
        : o.element !== undefined ? [o.element] : [];
    if (!raws.length)
        return null;
    // All or nothing. Half a group is worse than none — the user would be left to
    // work out which of their elements went missing, and why.
    const elements = [];
    for (const r of raws) {
        const parsed = parseClipElement(r);
        if (!parsed)
            return null;
        elements.push(parsed);
    }
    return { boardId: o.boardId, elements };
}
/** One element off the clipboard, validated. */
function parseClipElement(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const e = raw;
    const num = (x) => (typeof x === 'number' && Number.isFinite(x) ? x : null);
    const x = num(e.x), y = num(e.y);
    if (x === null || y === null)
        return null;
    if (e.kind === 'image') {
        if (typeof e.imageId !== 'string' || !e.imageId)
            return null;
        // w/h are placement hints, so a payload from an older version that lacks them
        // is still perfectly good — it just cannot be centred.
        const w = num(e.w) ?? 0, h = num(e.h) ?? 0;
        return { kind: 'image', imageId: e.imageId, x, y, w, h };
    }
    if (e.kind === 'note') {
        const w = num(e.w), h = num(e.h);
        if (w === null || h === null || w <= 0 || h <= 0)
            return null;
        if (typeof e.body !== 'string' || typeof e.color !== 'string')
            return null;
        return {
            kind: 'note',
            body: e.body.slice(0, FEEDBACK_NOTE_BODY_MAX),
            // An unknown key falls back to the default rather than reaching the DB.
            // The server coerces it again on create; this keeps the local paint honest.
            color: feedbackNoteColorDef(e.color).key,
            w, h, x, y,
        };
    }
    // Without this branch a link card was written to the clipboard perfectly well
    // and refused on the way back — and because the decode is all-or-nothing, ONE
    // link nulled the whole payload, so a mixed selection pasted nothing at all.
    // A lone link only seemed to work: its `text/plain` IS the URL, so the
    // unrelated "you pasted a URL" path quietly created a fresh card instead,
    // dropping any title the user had typed. (b-01908)
    if (e.kind === 'link') {
        const w = num(e.w), h = num(e.h);
        if (w === null || h === null || w <= 0 || h <= 0)
            return null;
        if (typeof e.url !== 'string')
            return null;
        // Re-validated, never trusted. This payload also travels in a `text/html`
        // span, so anything on the clipboard can claim to be one of ours, and a
        // pasted card is double-click-to-open — a `javascript:` URL smuggled in here
        // would be an XSS vector. Same gate a typed URL passes: http(s) only.
        const url = parseLinkPaste(e.url);
        if (!url)
            return null;
        return {
            kind: 'link',
            url,
            // Capped to what the server stores, so the clip cannot promise a title the
            // create would silently truncate.
            titleOverride: typeof e.titleOverride === 'string' && e.titleOverride.trim()
                ? e.titleOverride.slice(0, FEEDBACK_LINK_TITLE_MAX)
                : null,
            w, h, x, y,
        };
    }
    if (e.kind === 'connector') {
        // Both ends RE-VALIDATED, never trusted: this payload also travels in a
        // `text/html` span, so anything on the clipboard can claim to be one of ours,
        // and a NaN coordinate would put an end somewhere unreachable.
        const a = parseConnectorEnd(e.a);
        const b = parseConnectorEnd(e.b);
        if (!a || !b)
            return null;
        const w = num(e.w) ?? 0;
        const h = num(e.h) ?? 0;
        return {
            kind: 'connector',
            color: feedbackConnectorColorDef(typeof e.color === 'string' ? e.color : null).key,
            arrowA: e.arrowA === true,
            arrowB: e.arrowB === true,
            a,
            b,
            w, h, x, y,
        };
    }
    if (e.kind === 'text') {
        const w = num(e.w), h = num(e.h);
        if (w === null || h === null || w <= 0 || h <= 0)
            return null;
        if (typeof e.body !== 'string')
            return null;
        return {
            kind: 'text',
            // RE-SANITIZED, never trusted. This payload also rides in a `text/html`
            // span, so anything on the clipboard can claim to be one of ours — and
            // unlike a note's, this body is written into the DOM as markup. The server
            // sanitizes again on create; this is what keeps the OPTIMISTIC local paint
            // honest in between.
            body: sanitizeFeedbackRichText(e.body),
            fill: feedbackTextFillDef(typeof e.fill === 'string' ? e.fill : null).key,
            border: feedbackTextBorderDef(typeof e.border === 'string' ? e.border : null).key,
            // Absent on a clip written before leading existed, which falls back rather
            // than refusing the paste.
            leading: feedbackTextLeadingDef(typeof e.leading === 'string' ? e.leading : null).key,
            w: Math.max(FEEDBACK_TEXT_MIN_W, w),
            h: Math.max(FEEDBACK_TEXT_MIN_H, h),
            x,
            y,
        };
    }
    return null;
}
/** The top-left corner of everything in a clip — the point a paste is measured
 *  from, so a group keeps its shape wherever it lands. Taken from the whole set
 *  rather than from the first element copied: otherwise a group copied bottom-up
 *  would paste with its corner off the cursor by however far that element
 *  happened to sit. */
export function clipBounds(elements) {
    return {
        x: Math.min(...elements.map((e) => e.x)),
        y: Math.min(...elements.map((e) => e.y)),
    };
}
/** How much room a whole clip occupies, so a group can be centred on the cursor
 *  the way one element is. Measured across every element's far edge, not just the
 *  widest one's, because the group's extent is what the user sees. */
export function clipSize(elements) {
    const o = clipBounds(elements);
    return {
        w: Math.max(...elements.map((e) => e.x + (e.w || 0))) - o.x,
        h: Math.max(...elements.map((e) => e.y + (e.h || 0))) - o.y,
    };
}
/** Where each element goes when the clip is pasted at `at`: the group's top-left
 *  corner lands on the cursor and everything keeps its offset from it. A single
 *  element therefore lands exactly on the cursor, as it always did. */
export function pasteOffsets(elements, at) {
    const o = clipBounds(elements);
    return elements.map((e) => ({ x: at.x + (e.x - o.x), y: at.y + (e.y - o.y) }));
}
/** The payload hidden in an otherwise empty span, so `text/html` can carry it
 *  where a private MIME type may not survive. URI-encoded rather than base64:
 *  it is attribute-safe, and `btoa` would mangle any non-Latin-1 note text. */
export function feedbackClipToHtml(json) {
    return `<span data-pcp-feedback="${encodeURIComponent(json)}"></span>`;
}
/** Pull our payload back out of a `text/html` clipboard flavour. */
export function feedbackClipFromHtml(html) {
    if (!html)
        return null;
    const m = /data-pcp-feedback="([^"]*)"/.exec(html);
    if (!m)
        return null;
    try {
        return decodeURIComponent(m[1]);
    }
    catch {
        return null;
    }
}
/**
 * What ⌘V does. Two pastes compete for one shortcut on this canvas — "drop the
 * screenshot on my clipboard onto the board", which is how screenshots have
 * always arrived, and "duplicate the element I copied".
 *
 * Our own payload wins when present, because it is the specific signal: an image
 * flavour is what the clipboard offers ANY app, whereas the payload could only
 * have been put there by this canvas. In practice they never both appear —
 * copying an element writes no image flavour, and copying an image elsewhere
 * writes no payload — and because the clipboard is one slot, the most recent copy
 * is simply the one that is there.
 */
export function feedbackPasteIntent(hasPayload, hasImage, hasLink = false) {
    if (hasPayload)
        return 'element';
    if (hasImage)
        return 'image';
    // Last, because it is the least specific signal: a clipboard carrying an image
    // often carries its source URL as text too, and the image is what was copied.
    return hasLink ? 'link' : 'none';
}
/** How far down-right a repeated paste lands from the one before it, in world
 *  units. Enough to read as a new element at any sane zoom, small enough that the
 *  copy is still obviously related to what it came from. */
export const FEEDBACK_PASTE_STEP = 28;
/**
 * Where a pasted copy lands.
 *
 * Two rules, because a paste has two quite different origins:
 *
 *   POINTER OVER THE CANVAS — put the copy exactly where the user is pointing,
 *   centred on the cursor when the size is known. Pointing at a spot and pressing
 *   ⌘V is an instruction about position, so honouring it beats any offset scheme.
 *
 *   POINTER ELSEWHERE — a keyboard-only paste, or the cursor parked in the
 *   sidebar. There is no target, so fall back to cascading down-right of the
 *   SOURCE: the only other place that reads as "related to the thing I copied".
 *   Here the first paste must ALREADY be offset, because landing exactly on the
 *   original is the one outcome indistinguishable from nothing happening.
 *
 * `repeat` counts pastes of the same payload at the same target: 0 is the first.
 * Repeats always cascade, so hammering ⌘V never stacks copies invisibly on one
 * spot — the case where a paste that worked looks like a paste that failed.
 */
export function feedbackPasteTarget(opts) {
    const step = opts.step ?? FEEDBACK_PASTE_STEP;
    const n = Math.max(0, Math.floor(opts.repeat ?? 0));
    const cascade = (from, k) => ({ x: from.x + step * k, y: from.y + step * k });
    const p = opts.pointer;
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
        const w = opts.size?.w, h = opts.size?.h;
        // Centre on the cursor when the size is known; otherwise the cursor becomes
        // the top-left, which is what a drag-and-drop onto the canvas already does.
        const cx = w && Number.isFinite(w) && w > 0 ? p.x - w / 2 : p.x;
        const cy = h && Number.isFinite(h) && h > 0 ? p.y - h / 2 : p.y;
        // The copy must never come out sitting ON its original, and this is the common
        // case rather than a corner one: you click an element to select it, so the
        // cursor is already over it, and ⌘C then ⌘V without moving the mouse would
        // centre the copy almost exactly on the thing it came from — a paste that
        // worked, looking precisely like one that did not. Too close, and it cascades
        // instead. Any real distance is still honoured exactly.
        if (Math.abs(cx - opts.source.x) < step && Math.abs(cy - opts.source.y) < step) {
            return cascade(opts.source, n + 1);
        }
        return cascade({ x: cx, y: cy }, n);
    }
    return cascade(opts.source, n + 1);
}
// ─── Cropping: one handle drag (b-01879) ─────────────────────────
/**
 * One frame of a crop drag: where the window and the box end up when `handle` is
 * dragged to `pointer`.
 *
 * Pure, and extracted here on purpose. Living inline in the canvas, this
 * arithmetic contained a bug that made a crop a ONE-WAY DOOR — every drag could
 * shrink the window and none could widen it, so a screenshot could never be
 * restored to its full self by dragging. The cause was clamping the pointer to
 * the box before mapping it into image space, which by construction can only ever
 * name a point already inside the window. The clamp belongs on the RESULT, where
 * 0 and 1 are the real edges of the picture.
 *
 * `pointer` is a WORLD point (already snapped by the caller); `crop` and `box` are
 * both as they were when the gesture began, so every frame is computed from the
 * start rather than accumulated — a drag that goes out and comes back lands
 * exactly where it started.
 */
export function cropDrag(opts) {
    const { crop, box, handle, pointer } = opts;
    const clamp01 = (v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);
    // The pointer as a fraction of the ORIGINAL window, deliberately unclamped, then
    // mapped into image space and clamped THERE — see the note above.
    const img = boxToAnchor((pointer.x - box.x) / box.w, (pointer.y - box.y) / box.h, crop);
    const iu = clamp01(img.u);
    const iv = clamp01(img.v);
    let { u, v, w, h } = crop;
    // Each dragged edge moves; the opposite edge is held, which is what keeps the
    // un-dragged sides where they visibly are.
    if (handle.includes('w')) {
        const right = u + w;
        u = Math.min(iu, right - FEEDBACK_CROP_MIN);
        w = right - u;
    }
    if (handle.includes('e')) {
        w = Math.max(FEEDBACK_CROP_MIN, iu - u);
    }
    if (handle.includes('n')) {
        const bottom = v + h;
        v = Math.min(iv, bottom - FEEDBACK_CROP_MIN);
        h = bottom - v;
    }
    if (handle.includes('s')) {
        h = Math.max(FEEDBACK_CROP_MIN, iv - v);
    }
    const next = clampCrop({ u, v, w, h });
    // The box follows the window: its size scales with the window's, and its origin
    // shifts by however far the window's origin moved. Without this, cropping the
    // left edge would look like dragging the whole screenshot sideways.
    const scaleX = box.w / crop.w;
    const scaleY = box.h / crop.h;
    return {
        crop: next,
        box: {
            x: box.x + (next.u - crop.u) * scaleX,
            y: box.y + (next.v - crop.v) * scaleY,
            w: next.w * scaleX,
            h: next.h * scaleY,
        },
    };
}
/**
 * Whether a selection on the board outlives focus leaving the element that held
 * it. The board clears its selection when you move on (b-01897) — the point being
 * that a selection you cannot see you left would act behind your back, by editing
 * the note you are no longer looking at.
 *
 * "Moving on" is the hard part, and reading it off `document.activeElement` alone
 * gets it wrong twice — both ways round, both of which shipped:
 *
 *  - Focus DID land, on our own toolbar. Pressing Crop or a colour swatch moves
 *    focus to that button, and every action then deselected the very thing it was
 *    about: Crop cancelled crop mode in the tick it turned it on, and a note took
 *    exactly one colour before the swatch strip vanished. The toolbar acts ON the
 *    selection, so it is ours — the containment test just has to be asked about
 *    the whole shell rather than the canvas box inside it.
 *  - Focus landed on NOBODY. A button that disappears as a result of its own press
 *    ("Reset crop" un-crops and is gone) leaves focus on `<body>` by the time
 *    anyone looks, and so does a press on a button in a browser that never focuses
 *    buttons. Empty focus is not evidence of leaving, so the press decides
 *    instead: it is the thing that actually moved, and it is ours or it is not.
 *
 * A keyboard-only session has no press to fall back on (`null`); it keeps the
 * selection, because nothing observed says the user went anywhere.
 */
export function feedbackSelectionSurvives(change) {
    if (change.focusLanded)
        return change.focusIsOurs;
    return change.pressWasOurs !== false;
}
//# sourceMappingURL=feedback.js.map